import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from '../config';

const STORAGE_KEY = 'clara.qaManager.advancedView';
const SAVE_DEBOUNCE_MS = 500;

const AdvancedQAViewContext = createContext(null);

export const useAdvancedQAView = () => {
  const ctx = useContext(AdvancedQAViewContext);
  if (!ctx) {
    throw new Error('useAdvancedQAView must be used within AdvancedQAViewProvider');
  }
  return ctx;
};

const readLocal = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeLocal = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // ignore storage errors
  }
};

export const AdvancedQAViewProvider = ({ children }) => {
  const { user } = useAuth();
  const [enabled, setEnabledState] = useState(() => readLocal());
  const [isHydrated, setIsHydrated] = useState(false);
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  // Hydrate from backend once user is available — last-write-wins by updatedAt
  useEffect(() => {
    if (!user) {
      setIsHydrated(false);
      return;
    }

    const backendValue = !!user?.preferences?.qaAdvancedView;
    const backendUpdatedAt = user?.preferences?.qaAdvancedViewUpdatedAt
      ? new Date(user.preferences.qaAdvancedViewUpdatedAt).getTime()
      : 0;
    const localValue = readLocal();

    let localUpdatedAt = 0;
    try {
      const ts = localStorage.getItem(`${STORAGE_KEY}.updatedAt`);
      localUpdatedAt = ts ? parseInt(ts, 10) : 0;
    } catch {
      // ignore
    }

    if (backendUpdatedAt >= localUpdatedAt) {
      // Backend wins (or both empty) — adopt backend
      if (backendValue !== localValue) {
        writeLocal(backendValue);
        try { localStorage.setItem(`${STORAGE_KEY}.updatedAt`, String(backendUpdatedAt)); } catch {}
      }
      setEnabledState(backendValue);
      lastSavedRef.current = backendValue;
    } else {
      // Local is newer — push to backend
      lastSavedRef.current = backendValue;
      pushToBackend(localValue);
      setEnabledState(localValue);
    }

    setIsHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const pushToBackend = useCallback(async (value) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      await axios.put(
        `${API_URL}/api/auth/profile`,
        { preferences: { qaAdvancedView: value } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      lastSavedRef.current = value;
    } catch (err) {
      // Non-fatal — localStorage already has the value
      console.warn('Failed to persist Advanced QA View preference:', err?.message);
    }
  }, []);

  const setEnabled = useCallback((value) => {
    const next = !!value;
    setEnabledState(next);
    writeLocal(next);
    try { localStorage.setItem(`${STORAGE_KEY}.updatedAt`, String(Date.now())); } catch {}

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (lastSavedRef.current !== next) {
        pushToBackend(next);
      }
    }, SAVE_DEBOUNCE_MS);
  }, [pushToBackend]);

  // Cleanup pending save on unmount
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  const value = {
    enabled,
    setEnabled,
    isHydrated,
  };

  return (
    <AdvancedQAViewContext.Provider value={value}>
      {children}
    </AdvancedQAViewContext.Provider>
  );
};

export default AdvancedQAViewContext;
